/**
 * DropdownMenu organism — compound API for action / selection menus.
 * Content portals to document.body. Escape / arrows / focus restore included.
 * Joyride tooltips do not dismiss the menu on mousedown.
 */

import * as React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { cn } from "@/utils/cn";
import type {
  DropdownMenuProps,
  DropdownMenuTriggerProps,
  DropdownMenuContentProps,
  DropdownMenuItemProps,
  DropdownMenuLabelProps,
  DropdownMenuSubProps,
  DropdownMenuSubTriggerProps,
  DropdownMenuSubContentProps,
  DropdownMenuSide,
} from "./DropdownMenu.types";
import { getDropdownMenuContentClasses } from "./DropdownMenu.styles";
import {
  childRefOf,
  composeRefs,
  computePosition,
  getEnabledItems,
  resolveSubmenuSide,
} from "./DropdownMenu.utils";

type MenuContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  closeMenu: (restoreFocus?: boolean) => void;
  deferFirstItemFocusRef: React.MutableRefObject<boolean>;
  skipInitialItemFocusRef: React.MutableRefObject<boolean>;
  rootRef: React.RefObject<HTMLDivElement | null>;
  triggerRef: React.RefObject<HTMLElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  menuId: string;
};

const DropdownMenuContext = React.createContext<MenuContextValue | null>(null);

function useDropdownMenu() {
  const ctx = React.useContext(DropdownMenuContext);
  if (!ctx) throw new Error("DropdownMenu components must be used within DropdownMenu");
  return ctx;
}

function focusTrigger(triggerRef: React.RefObject<HTMLElement | null>) {
  requestAnimationFrame(() => {
    triggerRef.current?.focus();
  });
}

function DropdownMenuRoot({
  children,
  open: controlledOpen,
  onOpenChange,
  className,
}: DropdownMenuProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const deferFirstItemFocusRef = React.useRef(false);
  const skipInitialItemFocusRef = React.useRef(false);
  const menuId = React.useId();

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = React.useCallback(
    (value: boolean) => {
      if (isControlled) onOpenChange?.(value);
      else setInternalOpen(value);
    },
    [isControlled, onOpenChange],
  );

  const closeMenu = React.useCallback(
    (restoreFocus = true) => {
      setOpen(false);
      if (restoreFocus) focusTrigger(triggerRef);
    },
    [setOpen],
  );

  const value = React.useMemo(
    () => ({
      open,
      setOpen,
      closeMenu,
      deferFirstItemFocusRef,
      skipInitialItemFocusRef,
      rootRef,
      triggerRef,
      contentRef,
      menuId,
    }),
    [open, setOpen, closeMenu, menuId],
  );

  return (
    <DropdownMenuContext.Provider value={value}>
      <div ref={rootRef} className={cn("relative inline-block", className)}>
        {children}
      </div>
    </DropdownMenuContext.Provider>
  );
}

function DropdownMenuTrigger({ children, asChild, className }: DropdownMenuTriggerProps) {
  const { open, setOpen, closeMenu, deferFirstItemFocusRef, skipInitialItemFocusRef, triggerRef, menuId } =
    useDropdownMenu();

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (open) {
        closeMenu(true);
        return;
      }
      deferFirstItemFocusRef.current = true;
      skipInitialItemFocusRef.current = false;
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      deferFirstItemFocusRef.current = false;
      skipInitialItemFocusRef.current = false;
      setOpen(true);
    }
  };

  const aria = {
    "aria-haspopup": "menu" as const,
    "aria-expanded": open,
    "aria-controls": menuId,
  };

  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<{
      onClick?: (e: React.MouseEvent) => void;
      onKeyDown?: (e: React.KeyboardEvent) => void;
    }>;

    return React.cloneElement(child, {
      ...aria,
      ref: composeRefs(triggerRef, childRefOf(child)),
      onClick: (e: React.MouseEvent) => {
        child.props.onClick?.(e);
        deferFirstItemFocusRef.current = false;
        skipInitialItemFocusRef.current = true;
        if (open) closeMenu(true);
        else setOpen(true);
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        child.props.onKeyDown?.(e);
        onKeyDown(e);
      },
    } as Partial<typeof child.props> & { ref: React.Ref<HTMLElement> });
  }

  return (
    <div
      ref={triggerRef as React.RefObject<HTMLDivElement>}
      role="button"
      tabIndex={0}
      onClick={() => {
        deferFirstItemFocusRef.current = false;
        skipInitialItemFocusRef.current = true;
        if (open) closeMenu(true);
        else setOpen(true);
      }}
      onKeyDown={onKeyDown}
      className={cn("cursor-pointer outline-none", className)}
      {...aria}
    >
      {children}
    </div>
  );
}

function DropdownMenuContent({
  children,
  align = "end",
  sideOffset = 4,
  className,
}: DropdownMenuContentProps) {
  const {
    open,
    closeMenu,
    deferFirstItemFocusRef,
    skipInitialItemFocusRef,
    rootRef,
    triggerRef,
    contentRef,
    menuId,
  } = useDropdownMenu();
  const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null);

  const updatePosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    const content = contentRef.current;
    if (!trigger || !content) return;
    const next = computePosition(trigger, content, align, sideOffset);
    setCoords((prev) =>
      prev && prev.top === next.top && prev.left === next.left ? prev : next,
    );
  }, [align, sideOffset, triggerRef, contentRef]);

  React.useLayoutEffect(() => {
    if (!open) {
      setCoords((prev) => (prev === null ? prev : null));
      return;
    }
    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    const content = contentRef.current;
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && content
        ? new ResizeObserver(updatePosition)
        : null;
    if (content) resizeObserver?.observe(content);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      cancelAnimationFrame(raf);
      resizeObserver?.disconnect();
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  React.useEffect(() => {
    if (!open) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (contentRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest(".react-joyride__tooltip")) return;
      closeMenu(true);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMenu(true);
        return;
      }

      const items = getEnabledItems(contentRef.current);
      if (items.length === 0) return;
      const active = document.activeElement as HTMLElement | null;
      const index = items.findIndex((el) => el === active);

      if (e.key === "ArrowDown") {
        e.preventDefault();
        const next = index < 0 ? 0 : (index + 1) % items.length;
        items[next]?.focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const next = index < 0 ? items.length - 1 : (index - 1 + items.length) % items.length;
        items[next]?.focus();
      } else if (e.key === "Home") {
        e.preventDefault();
        items[0]?.focus();
      } else if (e.key === "End") {
        e.preventDefault();
        items[items.length - 1]?.focus();
      } else if (e.key === "Tab") {
        closeMenu(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, closeMenu, rootRef, contentRef, triggerRef]);

  React.useEffect(() => {
    if (!open) return;

    const focusFirst = () => {
      getEnabledItems(contentRef.current)[0]?.focus();
    };

    if (deferFirstItemFocusRef.current) {
      const onKeyUp = (e: KeyboardEvent) => {
        if (e.key !== " " && e.key !== "Enter") return;
        deferFirstItemFocusRef.current = false;
        focusFirst();
        document.removeEventListener("keyup", onKeyUp);
      };
      document.addEventListener("keyup", onKeyUp);
      return () => {
        document.removeEventListener("keyup", onKeyUp);
      };
    }

    // Pointer open: focus the panel, not an item — otherwise the first item
    // keeps :focus background after the pointer moves to another row.
    if (skipInitialItemFocusRef.current) {
      skipInitialItemFocusRef.current = false;
      const id = requestAnimationFrame(() => {
        contentRef.current?.focus();
      });
      return () => cancelAnimationFrame(id);
    }

    const id = requestAnimationFrame(focusFirst);
    return () => cancelAnimationFrame(id);
  }, [open, contentRef, deferFirstItemFocusRef, skipInitialItemFocusRef]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          ref={contentRef}
          id={menuId}
          role="menu"
          tabIndex={-1}
          initial={{ opacity: 0, scale: 0.96, y: -4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: -4 }}
          transition={{ duration: 0.15 }}
          style={{
            position: "fixed",
            top: coords?.top ?? -9999,
            left: coords?.left ?? -9999,
            visibility: coords ? "visible" : "hidden",
          }}
          className={getDropdownMenuContentClasses(className)}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

function DropdownMenuGroup({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div role="group" className={cn("py-0.5", className)}>{children}</div>;
}

const DropdownMenuItem = React.forwardRef<HTMLButtonElement, DropdownMenuItemProps>(
  ({ className, variant = "default", inset, disabled, onClick, ...props }, ref) => {
    const { closeMenu } = useDropdownMenu();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      onClick?.(e);
      closeMenu(true);
    };

    return (
      <button
        ref={ref}
        type="button"
        role="menuitem"
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onPointerMove={(e) => {
          if (disabled) return;
          if (document.activeElement !== e.currentTarget) {
            e.currentTarget.focus({ preventScroll: true });
          }
        }}
        className={cn(
          "relative flex w-full select-none items-center gap-3 rounded-md px-3 py-2.5 text-sm outline-none transition-colors whitespace-nowrap",
          "cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:focus-visible:bg-transparent",
          inset && "pl-7",
          variant === "default" && "text-grey hover:bg-purple-soft/50 focus-visible:bg-purple-soft/50",
          variant === "destructive" && "text-red hover:bg-red-soft/50 focus-visible:bg-red-soft/50 [&_svg]:text-red",
          "[&_svg]:shrink-0 [&_svg]:size-4",
          className,
        )}
        data-disabled={disabled ?? undefined}
        {...props}
      />
    );
  },
);
DropdownMenuItem.displayName = "DropdownMenuItem";

function DropdownMenuLabel({ className, inset, ...props }: DropdownMenuLabelProps) {
  return (
    <div
      className={cn("px-3 py-2 text-xs tracking-wider font-medium text-grey-muted", inset && "pl-7", className)}
      {...props}
    />
  );
}

function DropdownMenuSeparator({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div role="separator" className={cn("my-1 h-px bg-light-lin", className)} {...props} />;
}

function DropdownMenuShortcut({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn("ml-auto text-xs tracking-widest text-grey", className)} {...props} />;
}

type SubContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  contentId: string;
};

const DropdownMenuSubContext = React.createContext<SubContextValue | null>(null);

function useDropdownMenuSub() {
  const ctx = React.useContext(DropdownMenuSubContext);
  if (!ctx) {
    throw new Error("DropdownMenu.Sub components must be used within DropdownMenu.Sub");
  }
  return ctx;
}

function DropdownMenuSub({
  children,
  open: controlledOpen,
  onOpenChange,
}: DropdownMenuSubProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentId = React.useId();
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = React.useCallback(
    (value: boolean) => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (isControlled) onOpenChange?.(value);
      else setInternalOpen(value);
    },
    [isControlled, onOpenChange],
  );

  const scheduleClose = React.useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  }, [setOpen]);

  React.useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const value = React.useMemo(
    () => ({ open, setOpen, contentId }),
    [open, setOpen, contentId],
  );

  return (
    <DropdownMenuSubContext.Provider value={value}>
      <div
        className="relative"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={scheduleClose}
      >
        {children}
      </div>
    </DropdownMenuSubContext.Provider>
  );
}

const DropdownMenuSubTrigger = React.forwardRef<
  HTMLButtonElement,
  DropdownMenuSubTriggerProps
>(({ className, inset, disabled, children, onClick, onKeyDown, ...props }, ref) => {
  const { open, setOpen, contentId } = useDropdownMenuSub();

  return (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={contentId}
      onClick={(e) => {
        if (disabled) return;
        onClick?.(e);
        setOpen(!open);
      }}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (disabled || e.defaultPrevented) return;
        if (e.key === "ArrowRight" || e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setOpen(true);
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          setOpen(false);
        }
      }}
      onPointerMove={(e) => {
        if (disabled) return;
        if (document.activeElement !== e.currentTarget) {
          e.currentTarget.focus({ preventScroll: true });
        }
        setOpen(true);
      }}
      className={cn(
        "relative flex w-full select-none items-center gap-3 rounded-md px-3 py-2.5 text-sm outline-none transition-colors whitespace-nowrap",
        "cursor-pointer disabled:cursor-not-allowed disabled:opacity-50",
        inset && "pl-7",
        "text-grey hover:bg-purple-soft/50 focus-visible:bg-purple-soft/50",
        "[&_svg]:shrink-0 [&_svg]:size-4",
        className,
      )}
      data-disabled={disabled ?? undefined}
      {...props}
    >
      <span className="flex-1 text-left">{children}</span>
      <ChevronRight className="ml-auto opacity-60" aria-hidden />
    </button>
  );
});
DropdownMenuSubTrigger.displayName = "DropdownMenuSubTrigger";

function DropdownMenuSubContent({
  children,
  className,
  sideOffset = 4,
}: DropdownMenuSubContentProps) {
  const { open, contentId, setOpen } = useDropdownMenuSub();
  const subContentRef = React.useRef<HTMLDivElement>(null);
  // null until measured — the panel stays hidden so it never paints off-screen.
  const [side, setSide] = React.useState<DropdownMenuSide | null>(null);

  React.useLayoutEffect(() => {
    if (!open) {
      setSide((prev) => (prev === null ? prev : null));
      return;
    }
    const content = subContentRef.current;
    // Parent is the Sub wrapper, whose box is the trigger row (content is absolute).
    const anchor = content?.parentElement;
    if (!content || !anchor) return;

    const updateSide = () => setSide(resolveSubmenuSide(anchor, content, sideOffset));
    updateSide();
    window.addEventListener("resize", updateSide);
    window.addEventListener("scroll", updateSide, true);
    return () => {
      window.removeEventListener("resize", updateSide);
      window.removeEventListener("scroll", updateSide, true);
    };
  }, [open, sideOffset]);

  if (!open) return null;

  return (
    <div
      ref={subContentRef}
      id={contentId}
      role="menu"
      data-side={side ?? undefined}
      style={side === "left" ? { marginRight: sideOffset } : { marginLeft: sideOffset }}
      className={cn(
        getDropdownMenuContentClasses(className),
        "absolute top-0 z-[1] max-h-[min(320px,70vh)] overflow-y-auto",
        side === "left" ? "right-full" : "left-full",
        side === null && "invisible",
      )}
      onMouseEnter={() => setOpen(true)}
    >
      {children}
    </div>
  );
}

export const DropdownMenu = Object.assign(DropdownMenuRoot, {
  Trigger: DropdownMenuTrigger,
  Content: DropdownMenuContent,
  Group: DropdownMenuGroup,
  Item: DropdownMenuItem,
  Label: DropdownMenuLabel,
  Separator: DropdownMenuSeparator,
  Shortcut: DropdownMenuShortcut,
  Sub: DropdownMenuSub,
  SubTrigger: DropdownMenuSubTrigger,
  SubContent: DropdownMenuSubContent,
});

export { useDropdownMenu };
